// A league's times between two instants: what the calendar shows of a league
// before anything of one reader's (PLAN-2026-10-30-the-calendar §4, scenario
// D12). Pure: a calendar, the games and the rules in, the times out.
//
// THE PLANNER'S OWN HELPERS. Every lock, waiver run, deadline, draft and week
// end here is computed by the function the planner plans its job with
// (`planJobs`), so the calendar cannot say a time the clock does not act at;
// `agenda.test.ts` holds the two to each other over a season.

import {
  DRAFT_ROOM_OPENS_MS,
  taxiClosesAt,
  taxiDeadlineWeek,
  type CalendarKickoff,
  type DraftMode,
  type LeagueSettings,
} from '@fsp/domain';
import type { Game } from '@fsp/sports-core';
import type { StoredPeriod } from './calendar';
import { firstLockAt, gamesInWindow, lockFromKickoff, waiverRunsBetween } from './read';

export interface AgendaInput {
  settings: LeagueSettings;
  /** The league's stored calendar, in order; empty before its draft makes one. */
  periods: readonly StoredPeriod[];
  /** The league's season of pro games. */
  games: readonly Game[];
  /** The draft still to come (`LeagueRef.draftAt`): null once it has run, or with no time. */
  draftAt: string | null;
  /** Inclusive, in milliseconds. */
  from: number;
  /** Exclusive. */
  to: number;
}

export interface AgendaWeek {
  period: number;
  label: string;
  start: string;
  end: string;
  kind: 'regular' | 'playoff' | 'off';
  /** Every kickoff of the week's games, in order, with how many start then. */
  slate: CalendarKickoff[];
  /** The kickoff with the most games, the earliest on a tie; null with no games. */
  mainSlateAt: string | null;
  /** The first instant anything in the week locks; null with no games. */
  firstLockAt: string | null;
}

export type AgendaEvent =
  | { kind: 'lineup-lock'; at: string; period: number }
  | { kind: 'waivers'; at: string; period: number | null }
  | { kind: 'deadline'; at: string; period: number | null; what: 'trades' | 'taxi' }
  | { kind: 'draft'; at: string; period: null; mode: DraftMode; roomOpensAt: string | null }
  | { kind: 'week-ends'; at: string; period: number }
  | { kind: 'playoffs'; at: string; period: number };

export interface LeagueAgenda {
  /** Each week that overlaps the range, in order. */
  weeks: AgendaWeek[];
  /** The league's own times in the range, in order. */
  events: AgendaEvent[];
  /** Each slot locks at its own player's kickoff, so the kickoffs are the locks. */
  perPlayerLocks: boolean;
  /** Claims are decided as players clear, so there are no runs: each claim is its own time. */
  continuous: boolean;
}

const ms = (at: string): number => Date.parse(at);
const iso = (at: number): string => new Date(at).toISOString();

/**
 * The week's kind as the calendar was laid with it (`periodKind`, stored on
 * the row); a week a consolation bracket plays is a playoff week here.
 */
function weekKind(p: StoredPeriod): AgendaWeek['kind'] {
  if (p.kind === 'off') return 'off';
  return p.kind === 'regular' ? 'regular' : 'playoff';
}

/** Each kickoff instant of a week's games and how many start then, in order. */
export function slateOf(period: StoredPeriod, games: readonly Game[]): CalendarKickoff[] {
  const counts = new Map<number, number>();
  for (const window of period.windows)
    for (const g of gamesInWindow(window, games))
      counts.set(ms(g.start), (counts.get(ms(g.start)) ?? 0) + 1);
  return [...counts.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([at, n]) => ({ at: iso(at), games: n }));
}

/**
 * THE WEEK'S MAIN SLATE: the kickoff with the most games, the earliest of
 * them on a tie. A game before it is early — a Thursday night, a morning
 * game abroad, a holiday or a Saturday — worked out from the games and
 * never from a day's name, so it holds for any sport.
 */
export function mainSlateOf(slate: readonly CalendarKickoff[]): string | null {
  let best: CalendarKickoff | null = null;
  for (const k of slate) if (best === null || k.games > best.games) best = k;
  return best?.at ?? null;
}

export function leagueAgenda(input: AgendaInput): LeagueAgenda {
  const { settings, periods, games, from, to } = input;
  const inRange = (at: number) => at >= from && at < to;
  const weekAt = (at: number): number | null =>
    periods.find((p) => ms(p.start) <= at && at < ms(p.end))?.index ?? null;
  const events: AgendaEvent[] = [];
  const perPlayerLocks = settings.lineup.lockRule === 'per-player-game';
  const playing = periods.filter((p) => weekKind(p) !== 'off');

  const weeks: AgendaWeek[] = [];
  for (const p of periods) {
    const kind = weekKind(p);
    const off = kind === 'off';
    if (ms(p.start) < to && ms(p.end) > from) {
      const slate = slateOf(p, games);
      const locks = p.windows
        .map((w) => firstLockAt(settings, w, games))
        .filter((at): at is number => at !== null);
      weeks.push({
        period: p.index,
        label: p.label,
        start: p.start,
        end: p.end,
        kind,
        slate,
        mainSlateAt: mainSlateOf(slate),
        firstLockAt: locks.length > 0 ? iso(Math.min(...locks)) : null,
      });
    }
    // A WEEK THE LEAGUE DOES NOT PLAY has nothing of the league's in it: the
    // sport's weeks before a league begun mid-season, and after its last.
    if (off) continue;
    // THE WHOLE LINEUP'S LOCK, under a rule that has one (`planJobs`'s
    // `lineup.lock` at the window's lock). Under a per-player lock each
    // kickoff is its own, and is said with the players it locks.
    for (const w of p.windows) {
      const lock = lockFromKickoff(settings, w.firstKickoff);
      if (lock !== null && inRange(ms(lock)))
        events.push({ kind: 'lineup-lock', at: lock, period: p.index });
    }
    if (inRange(ms(p.end))) events.push({ kind: 'week-ends', at: p.end, period: p.index });
  }

  const firstPlayoff = playing.find((p) => weekKind(p) === 'playoff');
  if (firstPlayoff !== undefined && inRange(ms(firstPlayoff.start)))
    events.push({ kind: 'playoffs', at: firstPlayoff.start, period: firstPlayoff.index });

  // THE WAIVER RUNS, as the planner plans `waivers.process`, from the draft
  // that makes the calendar to the end of the league's last week: a league
  // with no rosters yet, or whose season is over, has no claims to decide.
  const continuous =
    settings.waivers.system !== 'none' && settings.waivers.processing.schedule === 'continuous';
  const last = playing[playing.length - 1];
  if (last !== undefined) {
    const until = Math.min(to, ms(last.end));
    if (until > from)
      for (const at of waiverRunsBetween(
        settings,
        periods,
        new Date(from - 1),
        new Date(until - 1),
      ))
        events.push({ kind: 'waivers', at: iso(at), period: weekAt(at) });
  }

  // THE TRADE DEADLINE: the end of its week, when the clock voids the offers
  // still open (`trades.deadline`).
  const deadline = settings.trades.deadlinePeriod;
  const deadlineWeek = deadline === null ? undefined : periods.find((p) => p.index === deadline);
  if (deadlineWeek !== undefined && inRange(ms(deadlineWeek.end)))
    events.push({
      kind: 'deadline',
      at: deadlineWeek.end,
      period: deadlineWeek.index,
      what: 'trades',
    });

  // THE TAXI SQUAD CLOSES at the first kickoff of its week (RULES_SCHEMA §2.7
  // rule 4), read as the lineup's save reads it (`taxiClosesAt`).
  const taxi = settings.roster.taxi;
  if (taxi !== null && taxi.count > 0) {
    const closes = taxiClosesAt(
      periods.map((p) => ({
        period: p.index,
        off: weekKind(p) === 'off',
        firstKickoff: p.windows[0]?.firstKickoff ?? null,
      })),
      taxiDeadlineWeek(taxi.deadlinePeriod, settings.schedule.regularSeason),
    );
    if (closes !== null && inRange(ms(closes)))
      events.push({ kind: 'deadline', at: closes, period: weekAt(ms(closes)), what: 'taxi' });
  }

  // THE DRAFT STILL TO COME, and for a live draft when its room opens.
  if (input.draftAt !== null && inRange(ms(input.draftAt))) {
    const mode = settings.draft.mode;
    events.push({
      kind: 'draft',
      at: input.draftAt,
      period: null,
      mode,
      roomOpensAt: mode === 'live' ? iso(ms(input.draftAt) - DRAFT_ROOM_OPENS_MS) : null,
    });
  }

  events.sort((a, b) => ms(a.at) - ms(b.at));
  return { weeks, events, perPlayerLocks, continuous };
}
